import {customThemeConstants} from '@hconnect/uikit/src/lib2'
import {ArrowBackIos, ArrowForwardIos, Cancel} from '@mui/icons-material'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import {Button, IconButton, Theme} from '@mui/material'
import Box from '@mui/material/Box'
import React, {useEffect, useState} from 'react'
import ReactDOM from 'react-dom'
import {useTranslation} from 'react-i18next'

import {SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE} from '../../consts'
import {AnomalyMediaItem} from '../../hooks/api/useAnomalyEventMedia'

function modulo(x: number, n: number) {
  return ((x % n) + n) % n
}

function hideAndRestoreMainContentScrollbarEffect() {
  const originalClassNames = document.body.className
  document.body.className = 'inactive-body'
  return () => {
    document.body.className = originalClassNames
  }
}

function downloadBase64(mediaData: string, fileName: string) {
  const decodedName = decodeURIComponent(fileName)
  const byteString = atob(mediaData)
  const bytes = new Uint8Array(byteString.length)
  for (let i = 0; i < byteString.length; i++) bytes[i] = byteString.charCodeAt(i)
  const blob = new Blob([bytes], {type: 'image/jpeg'})
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = decodedName
  link.click()
  URL.revokeObjectURL(url)
}

const ICON_SIZE = 'min(4vmax, 50px)'
const NAV_ICON_PADDING = 3

const iconNavTheme = (theme: Theme) => ({
  zIndex: 1550,
  color: theme.palette.primary.main,
  position: 'fixed' as const,
  top: `calc(50vh - ${ICON_SIZE})`,
  cursor: 'pointer',
  '& svg': {fontSize: ICON_SIZE}
})

type Props = {
  mediaItems: AnomalyMediaItem[]
  activeIndex: number
  onClose: () => void
}

export const AnomalyMediaViewer: React.FC<Props> = ({mediaItems, activeIndex, onClose}) => {
  const {t} = useTranslation(SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE)
  const [selectedIndex, setSelectedIndex] = useState(activeIndex)
  const [imgError, setImgError] = useState(false)

  useEffect(hideAndRestoreMainContentScrollbarEffect, [])

  const item = mediaItems[selectedIndex]

  const step = (n: number) => (ev: React.SyntheticEvent) => {
    ev.stopPropagation()
    setSelectedIndex(modulo(selectedIndex + n, mediaItems.length))
    setImgError(false)
  }

  const domNode = document.getElementById('attachmentViewer')
  if (!item || !domNode) return null

  return ReactDOM.createPortal(
    <Box
      sx={{height: '100vh', maxHeight: '100vh'}}
      onClick={onClose}
      data-test-id="anomaly-media-viewer"
    >
      <Box
        display="flex"
        justifyContent="space-between"
        sx={{padding: customThemeConstants().defaultGridSpacing}}
      >
        <Button
          color="primary"
          variant="contained"
          startIcon={<FileDownloadIcon />}
          onClick={(e) => {
            e.stopPropagation()
            downloadBase64(item.mediaData, item.fileName)
          }}
          data-test-id="anomaly-media-download"
        >
          {t('shiftEvent.action.download')}
        </Button>
        <IconButton
          onClick={onClose}
          color="primary"
          data-test-id="anomaly-media-viewer-close"
        >
          <Cancel />
        </IconButton>
      </Box>

      {mediaItems.length > 1 && (
        <>
          <Box
            component="span"
            sx={(theme) => ({left: 0, py: 15, pl: NAV_ICON_PADDING, ...iconNavTheme(theme)})}
            onClick={step(-1)}
            data-test-id="anomaly-media-viewer-prev"
          >
            <ArrowBackIos />
          </Box>
          <Box
            component="span"
            sx={(theme) => ({
              right: 0,
              py: 15,
              pl: 7.5,
              pr: NAV_ICON_PADDING,
              ...iconNavTheme(theme)
            })}
            onClick={step(+1)}
            data-test-id="anomaly-media-viewer-next"
          >
            <ArrowForwardIos />
          </Box>
        </>
      )}

      <Box
        sx={{
          width: '100%',
          height: 'calc(100vh - 80px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {imgError ? (
          <InsertDriveFileIcon sx={{fontSize: 80, color: 'grey.500'}} />
        ) : (
          <img
            src={`data:image/jpeg;base64,${item.mediaData}`}
            alt={decodeURIComponent(item.fileName)}
            onError={() => setImgError(true)}
            style={{maxWidth: '100%', maxHeight: '100%', objectFit: 'contain'}}
          />
        )}
      </Box>
    </Box>,
    domNode
  )
}

export const MediaThumb: React.FC<{mediaItem: AnomalyMediaItem}> = ({mediaItem}) => {
  const [imgError, setImgError] = useState(false)
  const decodedName = decodeURIComponent(mediaItem.fileName)
  if (imgError) {
    return <InsertDriveFileIcon sx={{fontSize: 60, color: 'text.secondary', ml: 2, mb: 1}} />
  }
  return (
    <Box
      component="img"
      src={`data:image/jpeg;base64,${mediaItem.mediaData}`}
      alt={decodedName}
      onError={() => setImgError(true)}
      sx={{
        height: '110px',
        maxWidth: '330px',
        overflowX: 'hidden',
        marginLeft: 2,
        marginBottom: 1,
        objectFit: 'contain',
        overflowY: 'hidden',
        cursor: 'pointer',
        '&:hover': {opacity: 0.7}
      }}
    />
  )
}
